import { Link } from "react-router-dom";

/**
 * ============================================================================
 * 404 — the catch-all route (D-045).
 * ============================================================================
 *
 * WHAT THIS REPLACES. `firebase.json` used to rewrite every unmatched path to
 * `/index.html`, so `/no-such-route` answered **200 OK with the complete home
 * page**. That was invisible while the site was an empty shell and became a
 * real defect the moment D-042 filled the shell with content: a crawler
 * following a stale or mistyped link received a successful response carrying
 * 23,598 characters of duplicate home-page text, at a URL that means nothing.
 * The catch-all is gone; Firebase serves this page, pre-rendered to
 * `build/404.html`, with a genuine 404 status.
 *
 * IT CARRIES NO PROFESSIONAL CONTENT, and that is why the copy can live in the
 * component. Article II.2 governs facts about the author's career; "the page
 * you asked for is not here" is chrome, in the same class as the navbar's
 * labels or a button caption. Nothing here needs to survive a resume sync.
 *
 * THE LINKS ARE THE POINT. A 404 that offers only an apology sends the reader
 * back to the browser's Back button. These two are the site's real entry
 * points — the documentary itself, and the narratives — and they are react-
 * router `<Link>`s rather than anchors so the visitor moves without a second
 * document load, exactly as the navbar's route link does.
 *
 * NO `<h1>`. Every other route carries exactly one, and this route carries
 * none deliberately: it is not a document about a subject, and the heading
 * hierarchy would be claiming otherwise to a screen reader. The heading below
 * is an `<h2>` inside the section's own labelled region.
 */
const NotFound = (): JSX.Element => (
	<section
		className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-20 text-center sm:px-8 lg:py-28"
		aria-labelledby="notfound-heading"
	>
		{/* `--text-muted`, NOT `--text-faint`, and the difference was measured
		    rather than assumed. This eyebrow is the first thing on the site to
		    put a quiet text role directly on the PAGE GROUND: `--text-faint`
		    exists for the story cards' period line and D-037 raised it to
		    4.68:1 against `--surface-card` (#171721). The page ground
		    (`--bg-base`, #1c1c27) is lighter than that card face, and the same
		    token measures 4.44:1 on it — below AA, caught by the sweep in dark
		    at all three widths. The token is right for its backdrop and wrong
		    for this one, so the fix is the correct role here (7.06:1), not a
		    change to a token that 23 cards depend on. */}
		<p className="text-sm font-semibold uppercase tracking-[0.3em] text-[var(--text-muted)]">Error 404</p>

		<h2 id="notfound-heading" className="heading mt-4">
			<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
				Page
			</span>{" "}
			not found
		</h2>

		<p className="mt-6 max-w-xl text-sm leading-relaxed text-[var(--text-secondary)] sm:text-base">
			The address you followed does not match anything on this site. It may have been mistyped, or it may point at
			a page that has since moved.
		</p>

		<div className="mt-10 flex flex-wrap items-center justify-center gap-4">
			<Link
				to="/"
				className="rounded-full bg-[var(--accent-interactive)] px-6 py-3 text-sm font-semibold text-[var(--accent-interactive-contrast)] transition-opacity duration-200 hover:opacity-90"
			>
				Back to the home page
			</Link>
			<Link
				to="/stories"
				className="rounded-full border border-[var(--border-pill)] px-6 py-3 text-sm font-semibold text-[var(--text-secondary)] transition-colors duration-200 hover:border-[var(--border-card-hover)] hover:text-[var(--text-bright)]"
			>
				Read the career stories
			</Link>
		</div>
	</section>
);

export default NotFound;
